import {Component, input, output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DeviceSettingField, DeviceSettingObjectListItem} from '../shared/models/device-config.model';
import {violatesAscending} from '../shared/object-list-validation';

/** The colour an added row starts at when its field has no better default (a neutral, valid `#RRGGBB`). */
const DEFAULT_COLOR = '#808080';

/**
 * Editor for a device-config setting of type `objectlist` — an **ordered, variable-length list of objects**
 * whose per-item shape is declared by the catalog ({@link DeviceSettingDefinition.itemFields}), e.g. the table
 * idle-warning escalation levels (#201).
 *
 * It is fully catalog-driven: one control per declared item field (`bool`/`int`/`string`/`color`), so a new
 * `objectlist` setting with a different item schema renders here with no front-end change. Rows can be added,
 * removed and reordered; when the catalog declares a {@link strictlyAscendingBy} field the editor flags the rows
 * that break the rule so the admin sees it *while editing* rather than as a bare 422 after saving. The server
 * remains the authority — this is courtesy validation only.
 *
 * Every edit emits a **new** array with a **new** item object: the value handed in may be the shared catalog
 * default (the manager only shallow-clones the outer array), so in-place mutation would corrupt it for every
 * other layer.
 *
 * The list itself is not focusable/keyboard-reorderable beyond its buttons — every action (add/remove/move) is a
 * real `<button>`, and every control carries a label, so the whole editor is keyboard- and screen-reader-usable.
 */
@Component({
  selector: 'app-object-list-editor',
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="flex flex-col gap-2">
      @if (fields().length === 0) {
        <!-- Defensive: an objectlist entry without an item schema is a catalog authoring bug, not user input. -->
        <p class="text-sm text-gray-500 italic">Für diese Einstellung ist kein Zeilenschema hinterlegt.</p>
      } @else {
        @if (items().length === 0) {
          <p class="text-sm text-gray-500 italic py-6 border-2 border-dashed border-gray-100 rounded-2xl text-center">
            Keine Einträge — die Liste ist leer.
          </p>
        } @else {
          <ul class="flex flex-col gap-2">
            <!-- Tracked by index on purpose: rows are reordered and edited in place, and index-tracking reuses the
                 same DOM node so a focused input keeps its focus while its value updates. -->
            @for (item of items(); track $index; let rowIndex = $index) {
              <li class="rounded-2xl border p-3 shadow-sm transition-all duration-200"
                  [class.bg-white]="!rowInvalid(rowIndex)"
                  [class.border-gray-100]="!rowInvalid(rowIndex)"
                  [class.bg-red-50]="rowInvalid(rowIndex)"
                  [class.border-red-200]="rowInvalid(rowIndex)">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div class="flex grow flex-wrap gap-3 min-w-0">
                    @for (field of fields(); track field.key) {
                      <div class="min-w-0 grow basis-32">
                        <label class="block text-xs font-bold text-gray-700 mb-1 tracking-tight"
                               [for]="controlId(rowIndex, field.key)">{{ field.label }}</label>
                        @switch (field.type) {
                          @case ('bool') {
                            <input type="checkbox" [id]="controlId(rowIndex, field.key)" [disabled]="disabled()"
                                   [ngModel]="boolOf(item, field.key)" [ngModelOptions]="{standalone: true}"
                                   (ngModelChange)="setField(rowIndex, field.key, $event)"
                                   class="h-4 w-4 rounded border-gray-300 text-theme-primary-600 focus:ring-theme-primary-500 disabled:cursor-not-allowed"/>
                          }
                          @case ('int') {
                            <input type="number" [id]="controlId(rowIndex, field.key)" [disabled]="disabled()"
                                   [ngModel]="numberOf(item, field.key)" [ngModelOptions]="{standalone: true}"
                                   (ngModelChange)="setField(rowIndex, field.key, $event === null || $event === '' ? null : +$event)"
                                   class="block w-full px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"/>
                          }
                          @case ('color') {
                            <div class="flex items-center gap-2">
                              <input type="color" [id]="controlId(rowIndex, field.key)" [disabled]="disabled()"
                                     [ngModel]="colorOf(item, field.key)" [ngModelOptions]="{standalone: true}"
                                     (ngModelChange)="setField(rowIndex, field.key, $event)"
                                     class="h-9 w-12 shrink-0 cursor-pointer rounded-xl border border-gray-200 bg-white p-1 shadow-sm disabled:cursor-not-allowed disabled:opacity-40"/>
                              <span class="font-mono text-xs text-gray-600">{{ colorOf(item, field.key) }}</span>
                            </div>
                          }
                          @default {
                            <input type="text" [id]="controlId(rowIndex, field.key)" [disabled]="disabled()"
                                   [ngModel]="textOf(item, field.key)" [ngModelOptions]="{standalone: true}"
                                   (ngModelChange)="setField(rowIndex, field.key, $event)"
                                   class="block w-full px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"/>
                          }
                        }
                      </div>
                    }
                  </div>

                  <div class="flex shrink-0 items-center gap-1" role="group"
                       [attr.aria-label]="'Zeile ' + (rowIndex + 1) + ' bearbeiten'">
                    <button type="button" (click)="moveUp(rowIndex)" [disabled]="disabled() || rowIndex === 0"
                            [attr.aria-label]="'Zeile ' + (rowIndex + 1) + ' nach oben'"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">
                      <svg class="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M8 3.5a.5.5 0 0 1 .354.146l4 4a.5.5 0 0 1-.708.708L8 4.707 4.354 8.354a.5.5 0 1 1-.708-.708l4-4A.5.5 0 0 1 8 3.5"/>
                      </svg>
                    </button>
                    <button type="button" (click)="moveDown(rowIndex)"
                            [disabled]="disabled() || rowIndex === items().length - 1"
                            [attr.aria-label]="'Zeile ' + (rowIndex + 1) + ' nach unten'"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">
                      <svg class="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M8 12.5a.5.5 0 0 1-.354-.146l-4-4a.5.5 0 0 1 .708-.708L8 11.293l3.646-3.647a.5.5 0 0 1 .708.708l-4 4A.5.5 0 0 1 8 12.5"/>
                      </svg>
                    </button>
                    <button type="button" (click)="removeRow(rowIndex)" [disabled]="disabled()"
                            [attr.aria-label]="'Zeile ' + (rowIndex + 1) + ' entfernen'"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-all duration-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                      <svg class="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1z"/>
                      </svg>
                    </button>
                  </div>
                </div>

                @if (rowInvalid(rowIndex)) {
                  <p class="mt-2 flex items-center gap-1 text-xs font-semibold text-red-700">
                    <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0-11a.9.9 0 0 0-.9.9l.25 3.5a.65.65 0 0 0 1.3 0l.25-3.5A.9.9 0 0 0 8 4m0 8a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8"/>
                    </svg>
                    {{ ascendingHint }}
                  </p>
                }
              </li>
            }
          </ul>
        }

        <div>
          <button type="button" (click)="addRow()" [disabled]="disabled()"
                  class="inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-gray-200 rounded-2xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 text-sm font-semibold shadow-sm transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed">
            <svg class="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/>
            </svg>
            Zeile hinzufügen
          </button>
        </div>
      }
    </div>
  `
})
export class ObjectListEditorComponent {
  /** The per-item schema from the catalog — one control is rendered per field, in this order. */
  readonly fields = input<readonly DeviceSettingField[]>([]);

  /** The current list value. Never mutated; every change is emitted as a fresh array. */
  readonly items = input<readonly DeviceSettingObjectListItem[]>([]);

  /** Mirrors the layer's "override this setting" checkbox: an inherited setting is read-only. */
  readonly disabled = input(false);

  /** The item field whose values must strictly ascend across the list (`null` = unconstrained). */
  readonly strictlyAscendingBy = input<string | null>(null);

  /** Emitted with the complete new list whenever a row is edited, added, removed or moved. */
  readonly itemsChange = output<DeviceSettingObjectListItem[]>();

  /** The inline message shown on a row that breaks the ascending rule. */
  protected get ascendingHint(): string {
    const field = this.fields().find(f => f.key === this.strictlyAscendingBy());
    return `„${field?.label ?? this.strictlyAscendingBy()}" muss größer sein als in der Zeile darüber.`;
  }

  /** A stable DOM id per row/field so every control can carry a real `<label for>`. */
  controlId(index: number, key: string): string {
    return `objectlist-${key}-${index}`;
  }

  /** Whether this row breaks the catalog's strictly-ascending rule (courtesy check; the server re-validates). */
  rowInvalid(index: number): boolean {
    return violatesAscending(this.items(), index, this.strictlyAscendingBy());
  }

  boolOf(item: DeviceSettingObjectListItem, key: string): boolean {
    return item[key] === true;
  }

  numberOf(item: DeviceSettingObjectListItem, key: string): number | null {
    const raw = item[key];
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  }

  textOf(item: DeviceSettingObjectListItem, key: string): string {
    const raw = item[key];
    return typeof raw === 'string' ? raw : '';
  }

  /**
   * The value for an `<input type="color">`, which only accepts a full `#RRGGBB`. Anything else (absent, a MAUI
   * `#AARRGGBB`, a shorthand) would silently render as black, so it falls back to a visible neutral instead.
   */
  colorOf(item: DeviceSettingObjectListItem, key: string): string {
    const raw = item[key];
    return typeof raw === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : DEFAULT_COLOR;
  }

  /** Replaces one field of one row (immutably) and emits the whole new list. */
  setField(index: number, key: string, value: unknown): void {
    if (this.disabled()) return;
    const next = this.items().map((item, i) =>
      i === index ? {...item, [key]: value as string | number | boolean} : {...item});
    this.itemsChange.emit(next);
  }

  /**
   * Appends a row seeded from the item schema. The strictly-ascending field starts one above the current maximum
   * so a freshly added row is valid instead of instantly flagged.
   */
  addRow(): void {
    if (this.disabled()) return;
    const items = this.items();
    const ascendingKey = this.strictlyAscendingBy();

    const row: DeviceSettingObjectListItem = {};
    for (const field of this.fields()) {
      switch (field.type) {
        case 'bool':
          row[field.key] = false;
          break;
        case 'int':
          row[field.key] = field.key === ascendingKey ? this.nextAscendingValue(items, ascendingKey) : 0;
          break;
        case 'color':
          row[field.key] = DEFAULT_COLOR;
          break;
        default:
          row[field.key] = '';
      }
    }
    this.itemsChange.emit([...items.map(item => ({...item})), row]);
  }

  removeRow(index: number): void {
    if (this.disabled()) return;
    this.itemsChange.emit(this.items().filter((_, i) => i !== index).map(item => ({...item})));
  }

  moveUp(index: number): void {
    this.swap(index, index - 1);
  }

  moveDown(index: number): void {
    this.swap(index, index + 1);
  }

  /** One above the highest readable value in the ascending column (0-based list ⇒ `1` for an empty list). */
  private nextAscendingValue(items: readonly DeviceSettingObjectListItem[], key: string): number {
    const values = items
      .map(item => item[key])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    return values.length === 0 ? 0 : Math.max(...values) + 1;
  }

  private swap(from: number, to: number): void {
    const items = this.items();
    if (this.disabled() || to < 0 || to >= items.length) return;
    const next = items.map(item => ({...item}));
    [next[from], next[to]] = [next[to], next[from]];
    this.itemsChange.emit(next);
  }
}
